import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import searchIcon from "../../assets/images/search.svg";
import filtersIcon from "../../assets/images/filter-arrows.svg";
import { updateSelectedItem } from "../../features/slices/CommonSlice";
import { showFilterMenu } from "../../features/slices/FilterSlice";
import { searchGames, setGameFilters } from "../../features/slices/GamesSlice";
import { openLoginModal } from "../../features/slices/ModalSlice";
import {
  addGuestLastPlayed,
  relogin,
} from "../../features/slices/UserSlice";
import { buildPlayGameUrl } from "../../utils/playGameUrl";

const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

export default function SearchField({ isHome = false }) {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const searchedGames = useSelector((state) => state.game.searchedGames);
  const userLoggedIn = useSelector((state) => state.user.userLoggedIn);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // home: search shows a dropdown of matching games; all-games: the term is
  // pushed into gameFilters so the grid itself refetches from /games?search=
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!isHome) {
        dispatch(setGameFilters({ search: value.trim() }));
        return;
      }
      if (value.length >= MIN_SEARCH_LENGTH) {
        setLoading(true);
        setShowDropdown(true);
        dispatch(searchGames({ search: value }));
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const openFilters = () => {
    dispatch(showFilterMenu());
    dispatch(updateSelectedItem("filter"));
  };

  // logged-out users picking a game: remember it, then send them to login
  const saveGameAndLogin = (game) => {
    dispatch(addGuestLastPlayed(game));
    localStorage.setItem("game", JSON.stringify(game));
    dispatch(openLoginModal("login"));
    dispatch(relogin());
  };

  useEffect(() => {
    setSearchResults(searchedGames || []);
    setLoading(false);
  }, [searchedGames]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="container-fluid search-bar custom-padding">
      <div className="d-flex search-bar-item">
        <input
          type="text"
          placeholder="Search Your Favourite Game..."
          value={searchTerm}
          style={isHome ? { width: "100%" } : {}}
          onChange={handleSearchChange}
        />
        {loading ? (
          <div
            className="spinner-border float-right"
            style={{ width: "20px", height: "20px", marginTop: "18px" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        ) : (
          <img
            loading="lazy"
            className="float-right"
            style={isHome ? { right: "10px" } : {}}
            src={searchIcon}
            alt="search icon"
          />
        )}
        {!isHome && (
          <button
            className="standard-btn text-center mobile-filter"
            onClick={openFilters}
          >
            <img loading="lazy" src={filtersIcon} alt="filter" />
            Filter
          </button>
        )}
        {showDropdown &&
          (searchResults.length ? (
            <div
              ref={dropdownRef}
              className="search--results border border-light border-opacity-25 pt-5 px-3"
            >
              <ul>
                {searchResults.map((item, index) =>
                  userLoggedIn ? (
                    <a
                      href={buildPlayGameUrl(item)}
                      key={item.id || index}
                    >
                      <li className="pb-4">{item.name}</li>
                    </a>
                  ) : (
                    <li
                      style={{ listStyle: "none", cursor: "pointer" }}
                      className="pb-4"
                      key={item.id || index}
                      onClick={() => saveGameAndLogin(item)}
                    >
                      {item.name}
                    </li>
                  )
                )}
              </ul>
            </div>
          ) : (
            !loading && (
              <div
                ref={dropdownRef}
                className="search--results border border-light border-opacity-25 py-5 px-4"
              >
                Games Not Found!
              </div>
            )
          ))}
      </div>
    </div>
  );
}
